import React, { useContext, useEffect, useState, useRef } from "react";

import { Fragment } from "react";
import { useHistory } from "react-router-dom";
import TripContext from "../../context/trip/tripContext";
import AuthContext from "../../context/auth/authContext";
import AlertContext from "../../context/alert/alertContext";
import SpotReviewContext from "../../context/spot/spotReviewContext";
import SpotReviewDayGroups from "./SpotReviewDayGroups";
import SpotReviewComposerModal from "../spots/SpotReviewComposer/SpotReviewComposerModal";
import ExistingReviewPickerModal from "./ExistingReviewPickerModal";
import Box from '@mui/joy/Box';
import Avatar from '@mui/joy/Avatar';
import Typography from '@mui/joy/Typography';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PublishIcon from '@mui/icons-material/Publish';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

const TripDetails = ({ trip_id, fromPath }) => {
    const history = useHistory();
    const reviewFromPath = `/trip/${trip_id}${fromPath ? `?from=${encodeURIComponent(fromPath)}` : ''}`;

    const tripContext = useContext(TripContext);
    const { getTripForID, updateTrip, attachTripReview, detachTripReview, deleteTrip, publishTrip, tripDetail, loading } = tripContext;
    const authContext = useContext(AuthContext);
    const currentUserID = authContext && authContext.user && authContext.user.user_id;
    const alertContext = useContext(AlertContext);
    const { setAlert } = alertContext;
    const spotReviewContext = useContext(SpotReviewContext);

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const titleRef = useRef(null);

    const isOwner = currentUserID && tripDetail && tripDetail.owner_user_id === currentUserID;

    // Called when the "Add an Activity" composer completes inside trip edit mode.
    // Posts the review to the server then attaches it to this trip.
    const handleDraftSubmit = async (draft) => {
        const payload = {
            title: draft.title,
            content: draft.content,
            placeId: draft.placeId,
            visitStartTime: draft.visitStartTime,
            visitDuration: draft.visitDuration,
            price: draft.price,
            category: draft.category,
            placeTypes: draft.placeTypes || [],
            photos: (draft.files || []).filter(Boolean),
            tripOnly: true,
        };
        const saved = await spotReviewContext.addSpotReview(payload);
        if (saved && saved.id) {
            const result = await attachTripReview(trip_id, saved.id);
            if (result.ok) {
                setAlert("Activity added to trip", "success");
                getTripForID(trip_id);
            } else {
                setAlert(result.msg || "Failed to attach activity to trip", "error");
            }
        } else {
            setAlert("Failed to create activity", "error");
        }
    };

    const handleEditStart = () => {
        setEditing(true);
    };

    const handleEditSave = async () => {
        const newTitle = titleRef.current ? titleRef.current.innerText.trim() : tripDetail.title;
        if (!newTitle) {
            setAlert("Title cannot be empty", "error");
            return;
        }
        setSaving(true);
        const result = await updateTrip(trip_id, { title: newTitle });
        setSaving(false);
        if (result.ok) {
            setEditing(false);
            setAlert("Trip updated", "success");
        } else {
            setAlert(result.msg || "Failed to update trip", "error");
        }
    };

    const handleEditCancel = () => {
        if (titleRef.current) titleRef.current.innerText = tripDetail.title || '';
        setEditing(false);
    };

    const handleDeleteTrip = async () => {
        if (!window.confirm('Delete this trip?')) return;
        await deleteTrip(trip_id);
        setAlert("Trip deleted", "success");
        history.push('/my-journeys');
    };

    const isDraft = tripDetail && tripDetail.visibility === 'draft';

    const handlePublish = async () => {
        if (!window.confirm('Publish this trip? It will become visible to everyone.')) return;
        const result = await publishTrip(trip_id);
        if (result.ok) {
            setAlert("Trip published!", "success");
        } else {
            setAlert(result.msg || "Failed to publish", "error");
        }
    };

    const handleAttachExisting = async (review) => {
        const result = await attachTripReview(trip_id, review.id);
        if (result.ok) {
            setAlert("Activity added to trip", "success");
        } else {
            setAlert(result.msg || "Failed to add activity", "error");
        }
    };

    const handleDetach = async (reviewId) => {
        if (!window.confirm('Remove this activity from the trip?')) return;
        const result = await detachTripReview(trip_id, reviewId);
        if (result.ok) {
            setAlert("Activity removed from trip", "success");
        } else {
            setAlert(result.msg || "Failed to remove activity", "error");
        }
    };

    useEffect(() => {
        getTripForID(trip_id);
        // eslint-disable-next-line
    }, []);

    if (!tripDetail && !loading) {
        return (
            <Fragment>
                <h4>No trip details to show</h4>
            </Fragment>
        );
    }

    if (!tripDetail || loading) {
        return <br />;
    }

    const firstReviewPhoto = (tripDetail.spot_reviews || [])
        .filter((r) => !r.deleted && r.photos && r.photos[0] && r.photos[0].image_link)
        .map((r) => r.photos[0].image_link)[0];
    const heroPhoto =
        (tripDetail.photos && tripDetail.photos.length > 0 && tripDetail.photos[0].image_link) ||
        firstReviewPhoto ||
        null;

    return (
        <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto' }}>
            {/* Hero header */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 180, sm: 220, md: 260 },
                    borderRadius: 'md',
                    overflow: 'hidden',
                    mb: 3,
                }}
            >
                {heroPhoto ? (
                    <Box
                        component="img"
                        src={heroPhoto}
                        alt={tripDetail.title}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: isDraft ? 'neutral.200' : 'primary.100',
                    }}>
                        {isDraft
                            ? <EditNoteIcon sx={{ fontSize: 80, color: 'neutral.400' }} />
                            : <FlightTakeoffIcon sx={{ fontSize: 80, color: 'primary.300' }} />}
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)',
                    }}
                />
                {/* Three-dot menu: top-right of hero card */}
                {isOwner && !editing && (
                    <Box sx={{ position: 'absolute', top: { xs: 4, sm: 8 }, right: { xs: 4, sm: 8 }, zIndex: 1 }}>
                        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.3)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            {isDraft && (
                                <MenuItem onClick={() => { setMenuAnchor(null); handlePublish(); }}>
                                    <ListItemIcon><PublishIcon fontSize="small" color="primary" /></ListItemIcon>
                                    <ListItemText sx={{ color: 'primary.main' }}>Publish</ListItemText>
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => { setMenuAnchor(null); handleEditStart(); }}>
                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => { setMenuAnchor(null); handleDeleteTrip(); }}>
                                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        p: { xs: 2, sm: 3 },
                        color: 'white',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {isDraft && (
                            <Chip label="Draft" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
                        )}
                        <Typography
                            level="h1"
                            ref={titleRef}
                            contentEditable={editing}
                            suppressContentEditableWarning
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleEditSave(); } }}
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                mb: 0.5,
                                outline: 'none',
                                borderBottom: editing ? '2px solid rgba(255,255,255,0.6)' : '2px solid transparent',
                                minWidth: 100,
                            }}
                        >
                            {tripDetail.title}
                        </Typography>
                    </Box>
                    {(tripDetail.destination_name || tripDetail.place_address) && (
                        <Typography
                            startDecorator={<LocationOnRoundedIcon />}
                            sx={{ color: 'rgba(255,255,255,0.85)', mb: 1.5 }}
                        >
                            {tripDetail.destination_name || tripDetail.place_address}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tripDetail.avatar && (
                            <Avatar
                                src={tripDetail.avatar.image_link}
                                size="sm"
                                sx={{ '--Avatar-size': '2rem' }}
                            />
                        )}
                        {(tripDetail.owner_name || tripDetail.owner_first_name) && (
                            <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                {tripDetail.owner_name || (tripDetail.owner_first_name + ' ' + (tripDetail.owner_last_name || ''))}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {tripDetail.content && (
                <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
                    {tripDetail.content}
                </Typography>
            )}

            <SpotReviewDayGroups
                spotReviews={tripDetail.spot_reviews}
                fromPath={reviewFromPath}
                readOnly={!editing}
                onDetach={editing ? handleDetach : undefined}
            />

            {editing && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <SpotReviewComposerModal
                            id={tripDetail.id}
                            isDraft={true}
                            onDraftSubmit={handleDraftSubmit}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, 24px)',
                                display: 'flex',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                '& > *': { pointerEvents: 'auto' },
                            }}
                        >
                            <ExistingReviewPickerModal
                                onPick={handleAttachExisting}
                                excludeIds={(tripDetail.spot_reviews || []).map((r) => r.id)}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 5 }}>
                        <button
                            onClick={handleEditSave}
                            disabled={saving}
                            style={{
                                padding: '10px 24px', borderRadius: 8, border: 'none',
                                backgroundColor: '#1976d2', color: 'white', fontWeight: 600,
                                cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9375rem',
                            }}
                        >
                            {saving ? 'Saving...' : 'Done'}
                        </button>
                        <button
                            onClick={handleEditCancel}
                            disabled={saving}
                            style={{
                                padding: '10px 16px', borderRadius: 8, border: '1px solid #ccc',
                                backgroundColor: 'transparent', color: '#666', fontWeight: 500,
                                cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9375rem',
                            }}
                        >
                            Cancel
                        </button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TripDetails;
